/**
 * `AskUserWizard` — the ask-user answer surface, wizard form (⚖ ratified
 * plan `lexide-ask-user-wizard-redesign-miniplan.md`; living contract
 * `lexide-ask-user-guide.md` §2–§3). Replaces the Phase A `AskUserCard`.
 *
 * RENDERED IN THE COMPOSER'S SLOT — the parent (`ChatInput`) unmounts the
 * textarea/attach/mic/send while this is active, so exactly ONE send
 * affordance exists at any moment (operator ruling, verbatim: "the wizard
 * should replace promptbox when active so user cannot enter anything on
 * the promptbox").
 *
 * ⚠ EXITS ARE LOAD-BEARING (`mission-ingest`'s accept condition): the
 * turn is NOT blocked (A-0.2), so a suppressed composer with no exit is a
 * modal trap. Esc, `Responder depois` AND the header ✕ all PARK (picks
 * preserved — the parent renders a pill and restores the composer).
 *
 * ⚖ ✕ PARKS, IT DOES NOT DESTROY (operator, 2026-08-17, on first use:
 * *"if I click close (X) the pending indicator disappears"*). The
 * universal close-gesture means "get this out of my way", not "delete
 * these questions" — putting DESTROY on it silently lost the ask with no
 * way back. Permanent dismissal lives on the PILL's own ✕ (ChatInput),
 * one deliberate step past closing. Do not move destruction back here.
 *
 * ⚖ GRAYSCALE ONLY (operator ruling): state is carried by VALUE and
 * WEIGHT, never hue — picked = filled dark chip; ⭐ badge = glyph +
 * border; descriptions one step lighter, never smaller.
 *
 * Operator-ratified enhancements (2026-08-17 compare-to-Claude-Code
 * pass): progress dots are CLICKABLE (jump to any question, picks
 * preserved); a question whose choices carry descriptions renders as
 * full-width ROWS instead of chips (`questionLayout`), with `Outro`
 * folded in as the last row; the park affordance shows a visible Esc
 * hint. Still NOT a tab strip — §5 of the plan rejects labeled tabs;
 * dot labels wait on the `header` schema handshake with mission-ingest.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { ArrowLeft, X } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  formatAnswersMessage,
  type AskUserCardData,
  type AskUserQuestion,
} from "@/lib/ask-user";
import {
  answeredCount,
  collectAnswers,
  dotLabel,
  goBack,
  goForward,
  goToStep,
  initWizardState,
  pickChoice,
  questionLayout,
  recommendedChoice,
  setOtherText,
  type WizardState,
} from "@/lib/workspace/ask-user-wizard-state";

export interface AskUserWizardProps {
  data: AskUserCardData;
  /** Posts the `[respostas]` message through the composer's own onSend. */
  onSubmit: (answersMessage: string) => void;
  /** Park — picks preserved by the PARENT keeping this mounted (hidden)
   *  per askId; the wizard itself only announces the intent. Esc, the
   *  header ✕ and `Responder depois` ALL route here — the wizard has NO
   *  destructive control (see the docblock ruling). */
  onPark: () => void;
}

/** Chip base — grayscale, state by value/weight only (⚖ palette ruling). */
const CHIP =
  "rounded-full border px-3 py-1.5 text-body transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-500/40";
/** Row variant — same palette, full width; a described choice needs a line. */
const ROW =
  "w-full rounded-lg border px-3 py-2 text-body text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-500/40";
const CHIP_IDLE =
  "border-cream-300/80 bg-transparent text-coffee-800 hover:bg-cream-100/60 dark:border-coffee-600/80 dark:text-cream-100 dark:hover:bg-coffee-800/60";
const CHIP_PICKED =
  "border-coffee-800 bg-coffee-800 font-medium text-cream-50 dark:border-cream-200 dark:bg-cream-200 dark:text-coffee-900";

export function AskUserWizard({ data, onSubmit, onPark }: AskUserWizardProps) {
  const questions = data.questions;
  const [state, setState] = useState<WizardState>(() => initWizardState(questions));
  const rootRef = useRef<HTMLDivElement>(null);

  // Focus moves INTO the wizard on mount so Esc works immediately and a
  // keyboard user is not stranded on an unmounted textarea.
  useEffect(() => {
    rootRef.current?.focus();
  }, []);

  const question: AskUserQuestion | undefined = questions[state.stepIndex];
  const total = questions.length;
  const isLast = state.stepIndex === total - 1;
  const answered = answeredCount(state, questions);
  const recommended = useMemo(
    () => (question ? recommendedChoice(question) : undefined),
    [question],
  );

  if (!question) return null; // zero questions cannot render a step

  const selection = state.selections[question.id] ?? { values: [], other: "" };
  const layout = questionLayout(question);

  function submit() {
    if (answered === 0) return;
    onSubmit(formatAnswersMessage(collectAnswers(state, questions)));
  }

  // Outro — free text, explicit confirm, never auto-advances. ONE element,
  // placed by layout: last row of the stack in row mode, below the chips
  // in chip mode (same testid either way — probes must not care).
  const otherInput =
    question.allowOther !== false ? (
      <input
        type="text"
        data-testid={`ask-user-wizard-other-${question.id}`}
        value={selection.other}
        onChange={(e) => setState((prev) => setOtherText(prev, question.id, e.target.value))}
        placeholder="Outro…"
        className={cn(
          "border border-cream-300/80 bg-transparent text-body text-coffee-900 placeholder:text-cream-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-500/40 dark:border-coffee-600/80 dark:text-cream-100 dark:placeholder:text-coffee-500",
          layout === "rows"
            ? "w-full rounded-lg px-3 py-2"
            : "w-full max-w-[420px] rounded-lg px-2.5 py-1",
        )}
      />
    ) : null;

  return (
    <div
      ref={rootRef}
      tabIndex={-1}
      data-testid="ask-user-wizard"
      aria-label={data.title ?? "Perguntas do assistente"}
      onKeyDown={(e) => {
        // Esc PARKS (never destroys) — guide §2. Every wizard exit parks;
        // permanent dismissal lives on the pill, one step further away.
        if (e.key === "Escape") {
          e.preventDefault();
          e.stopPropagation();
          onPark();
        }
      }}
      className="flex flex-col gap-2 outline-none"
    >
      {/* Header: title · progress dots · counter · exits */}
      <div className="flex items-center gap-2">
        <span className="text-body font-medium text-coffee-900 dark:text-cream-100">
          {data.title ?? "O assistente precisa da sua resposta"}
        </span>
        {/* Dots are CLICKABLE (⚖ enhancement pass): jump to any question,
            picks preserved. Label = the question's `topic` when the wire
            ships one (`5eeffd836`), numeric otherwise — a tooltip/aria
            label per dot, NOT a tab strip (§5's rejection stands). */}
        <span className="ml-auto flex items-center">
          {questions.map((q, i) => (
            <button
              key={q.id}
              type="button"
              data-testid={`ask-user-wizard-dot-${i}`}
              aria-label={dotLabel(q, i, total)}
              aria-current={i === state.stepIndex ? "step" : undefined}
              title={dotLabel(q, i, total)}
              onClick={() => setState((prev) => goToStep(prev, i, total))}
              className="group flex h-4 w-4 items-center justify-center rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-500/40"
            >
              <span
                className={cn(
                  "h-1.5 w-1.5 rounded-full transition-colors",
                  i === state.stepIndex
                    ? "bg-coffee-700 dark:bg-cream-200"
                    : "bg-cream-300 group-hover:bg-cream-400 dark:bg-coffee-600 dark:group-hover:bg-coffee-500",
                )}
              />
            </button>
          ))}
        </span>
        <span
          data-testid="ask-user-wizard-progress"
          className="text-caption tabular-nums text-cream-500 dark:text-coffee-500"
        >
          {state.stepIndex + 1}/{total}
        </span>
        <button
          type="button"
          data-testid="ask-user-wizard-close"
          onClick={onPark}
          title="Fechar (responder depois)"
          className="rounded p-1 text-cream-500 transition-colors hover:bg-cream-100/60 hover:text-coffee-700 dark:text-coffee-500 dark:hover:bg-coffee-800/60 dark:hover:text-cream-200"
        >
          <X size={14} />
        </button>
      </div>

      {/* The question + optional one-line explanation (⚖ D-4) */}
      <div data-testid={`ask-user-wizard-question-${question.id}`}>
        <p className="text-body text-coffee-900 dark:text-cream-100">
          {question.prompt}
          {question.multiSelect ? (
            <span className="ml-1 text-cream-500 dark:text-coffee-500">
              (pode escolher mais de um)
            </span>
          ) : null}
        </p>
        {question.explanation ? (
          <p
            data-testid="ask-user-wizard-explanation"
            className="mt-0.5 text-body text-coffee-600 dark:text-cream-300"
          >
            Por quê: {question.explanation}
          </p>
        ) : null}
      </div>

      {/* Choices — ADAPTIVE (⚖ enhancement pass): full-width ROWS when any
          choice carries a description (a described choice needs a full
          line), chips when none does. Either way the description is a
          VISIBLE second line, never a tooltip. In row mode `Outro` folds
          in as the last row; in chip mode it stays below the chips. */}
      <div
        className={layout === "rows" ? "flex flex-col gap-1.5" : "flex flex-wrap gap-2"}
      >
        {(question.choices ?? []).map((c) => {
          const picked = selection.values.includes(c.value);
          const isRecommended = recommended?.value === c.value;
          return (
            <button
              key={c.value}
              type="button"
              data-testid={`ask-user-wizard-choice-${question.id}-${c.value}`}
              aria-pressed={picked}
              onClick={() =>
                setState((prev) => pickChoice(prev, questions, question, c.value).state)
              }
              className={cn(
                layout === "rows" ? ROW : cn(CHIP, "text-left"),
                picked ? CHIP_PICKED : CHIP_IDLE,
              )}
            >
              <span className="flex items-center gap-1.5">
                {isRecommended ? <span aria-hidden="true">⭐</span> : null}
                <span>{c.label}</span>
                {isRecommended ? (
                  <span
                    data-testid="ask-user-wizard-recommended"
                    className={cn(
                      "rounded-full border px-1.5 text-caption font-medium",
                      picked
                        ? "border-cream-300/60 text-cream-200 dark:border-coffee-600 dark:text-coffee-700"
                        : "border-cream-400/80 text-coffee-700 dark:border-coffee-500 dark:text-cream-300",
                    )}
                  >
                    Recomendado
                  </span>
                ) : null}
              </span>
              {c.description ? (
                <span
                  className={cn(
                    "mt-0.5 block text-caption",
                    picked
                      ? "text-cream-300 dark:text-coffee-700"
                      : "text-coffee-600 dark:text-cream-300",
                  )}
                >
                  {c.description}
                </span>
              ) : null}
            </button>
          );
        })}
        {layout === "rows" && otherInput}
      </div>

      {layout === "chips" && otherInput}

      {/* Footer: Voltar · Responder depois · advance/submit */}
      <div className="flex items-center gap-2 pt-1">
        {state.stepIndex > 0 ? (
          <button
            type="button"
            data-testid="ask-user-wizard-back"
            onClick={() => setState((prev) => goBack(prev))}
            className="flex items-center gap-1 rounded px-2 py-1 text-body text-coffee-700 transition-colors hover:bg-cream-100/60 dark:text-cream-300 dark:hover:bg-coffee-800/60"
          >
            <ArrowLeft size={14} /> Voltar
          </button>
        ) : null}
        <button
          type="button"
          data-testid="ask-user-wizard-park"
          onClick={onPark}
          className="ml-auto flex items-center gap-1.5 rounded px-2 py-1 text-body text-coffee-600 transition-colors hover:bg-cream-100/60 hover:text-coffee-700 dark:text-cream-300 dark:hover:bg-coffee-800/60 dark:hover:text-cream-200"
        >
          Responder depois
          {/* Visible Esc hint (⚖ enhancement pass) — teaches the reflex
              key on the affordance it triggers; grayscale, caption-sized
              (a keycap, not body copy — no-tiny-fonts rule untouched). */}
          <kbd
            data-testid="ask-user-wizard-esc-hint"
            className="rounded border border-cream-300/80 px-1 font-sans text-caption text-cream-500 dark:border-coffee-600/80 dark:text-coffee-500"
          >
            Esc
          </kbd>
        </button>
        {isLast ? (
          <button
            type="button"
            data-testid="ask-user-wizard-submit"
            onClick={submit}
            disabled={answered === 0}
            className={cn(
              "rounded-lg px-3 py-1.5 text-body font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-500/40",
              answered > 0
                ? "bg-coffee-800 text-cream-50 hover:bg-coffee-700 dark:bg-cream-200 dark:text-coffee-900 dark:hover:bg-cream-100"
                : "bg-cream-200 text-cream-500 dark:bg-coffee-800 dark:text-coffee-500",
            )}
          >
            {`Enviar respostas${total > 1 ? ` (${answered}/${total})` : ""}`}
          </button>
        ) : (
          // Non-last steps ALWAYS carry an explicit advance: multi-select
          // never auto-advances, and a lawyer whose answer is Outro text
          // (or who wants to skip — unanswered questions are OMITTED from
          // the message, so skipping is legitimate) must never be
          // stranded on a step with no way forward. Single-select chip
          // picks still auto-advance past it (⚖ D-3).
          <button
            type="button"
            data-testid="ask-user-wizard-advance"
            onClick={() => setState((prev) => goForward(prev, total))}
            className="rounded-lg bg-coffee-800 px-3 py-1.5 text-body font-medium text-cream-50 transition-colors hover:bg-coffee-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-500/40 dark:bg-cream-200 dark:text-coffee-900 dark:hover:bg-cream-100"
          >
            Avançar
          </button>
        )}
      </div>
    </div>
  );
}
